import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { fetchProductDetail, lookupPricing, fetchPublicDealer } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useHouseBrand } from "@/hooks/useHouseBrand";
import {
  Loader2,
  AlertCircle,
  Star,
  ShoppingCart,
  ShoppingBag,
  Package,
  Beaker,
  Play,
} from "lucide-react";
import CartSheet from "@/components/DealerShop";
import { sanitizeHtml } from "@/lib/sanitize";
import ProductReviews from "@/components/ProductReviews";
import { ProductSchema } from "@/components/acp/ProductSchema";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

/**
 * Public product detail page for a dealer's shop.
 *
 * Route: /d/:slug/product/:productSlug
 *
 * Pulls product detail from `GET /api/postcue/product-detail/{slug_or_id}`
 * (caches 2 minutes server-side) and resolves role pricing when a dealer
 * is signed in.  Renders hero image, blurb/excerpt, rating, MSRP +
 * role price + sale price, ingredient cards, and the full Airtable
 * `post_content` HTML at the bottom (matches mindfulminerals.com).
 */
export default function DealerProductPage() {
  const params = useParams();
  const productSlug = params.productSlug;
  // /product/:productSlug (SEO URL, no dealer) → house store "orevital"
  const slug = params.slug || "orevital";
  const navigate = useNavigate();
  const { user } = useAuth();
  const brand = useHouseBrand();
  const [dealer, setDealer] = useState(null);
  const [product, setProduct] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [qty, setQty] = useState(1);
  const [activeMedia, setActiveMedia] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [pres, dres] = await Promise.all([
          fetchProductDetail(productSlug),
          fetchPublicDealer(slug).catch(() => null),
        ]);
        if (!active) return;
        setProduct(pres?.product || null);
        setActiveMedia(null);
        setDealer(dres || null);
        setError(null);
      } catch (e) {
        if (!active) return;
        setError(
          e?.response?.data?.detail || e.message || "Could not load product"
        );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug, productSlug]);

  // Resolve role pricing for signed-in dealers / spas / ambassadors.
  /* eslint-disable */
  useEffect(() => {
    const tier = (user?.role_name || user?.role || "").toLowerCase();
    if (!tier || !product?.id) return;
    let active = true;
    (async () => {
      try {
        const res = await lookupPricing({
          productRecordIds: [product.id],
          tier,
        });
        if (!active) return;
        setPricing((res?.items || [])[0] || null);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      active = false;
    };
  }, [user?.role_name, user?.role, product?.id]);
  /* eslint-enable */

  // Keep cart badge in sync
  useEffect(() => {
    function syncCount() {
      try {
        const raw = localStorage.getItem(`postcue.cart.${slug}`);
        const c = raw ? JSON.parse(raw) : {};
        setCartCount(
          Object.values(c).reduce((s, q) => s + (Number(q) || 0), 0)
        );
      } catch {
        setCartCount(0);
      }
    }
    syncCount();
    window.addEventListener("storage", syncCount);
    const id = setInterval(syncCount, 800);
    return () => {
      window.removeEventListener("storage", syncCount);
      clearInterval(id);
    };
  }, [slug]);

  function handleAddToCart() {
    if (!product) return;
    try {
      const key = `postcue.cart.${slug}`;
      const raw = localStorage.getItem(key);
      const cart = raw ? JSON.parse(raw) : {};
      cart[product.id] = (cart[product.id] || 0) + (Number(qty) || 1);
      localStorage.setItem(key, JSON.stringify(cart));
      setCartCount(
        Object.values(cart).reduce((s, q) => s + (Number(q) || 0), 0)
      );
    } catch {
      /* ignore */
    }
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/d/${slug}`)}`);
      return;
    }
    setCartOpen(true);
  }

  if (loading) {
    return (
      <div className="min-h-dvh bg-zinc-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-zinc-400" />
      </div>
    );
  }
  if (error || !product) {
    return (
      <div className="min-h-dvh bg-zinc-50 flex flex-col items-center justify-center px-6 text-center">
        <AlertCircle className="text-zinc-400 mb-3" size={32} />
        <div className="font-semibold text-zinc-900">Product not found</div>
        <p className="text-sm text-zinc-500 mt-1 max-w-sm">
          We couldn&apos;t find that product.
        </p>
        <Link
          to={`/d/${slug}`}
          className="text-sm text-violet-700 mt-4 hover:underline"
        >
          ← Back to shop
        </Link>
      </div>
    );
  }

  // Resolve display prices
  const msrp = Number(product.price || 0);
  const rolePrice = pricing?.role_price ? Number(pricing.role_price) : null;
  const showRolePrice =
    rolePrice !== null && rolePrice > 0 && Math.abs(rolePrice - msrp) > 0.005;
  const tier = (user?.role_name || user?.role || "").toLowerCase();
  const saleInfo = resolveSale(product.sale || {}, tier);

  // Build the media gallery: prefer the typed `media` list from the API
  // (each item is {type: 'image'|'video'|'embed', url}).
  const media =
    product.media && product.media.length
      ? product.media
      : product.image_url
        ? [{ type: "image", url: product.image_url }]
        : [];
  const current = activeMedia || media[0] || null;
  const hasGallery = media.length > 1;

  // Final price = sale > role > msrp
  const finalPrice = saleInfo.active
    ? saleInfo.salePrice
    : showRolePrice
      ? rolePrice
      : msrp;

  return (
    <>
    <ProductSchema product={product} price={msrp} url={params.slug ? `https://orevital.com/d/${slug}/product/${productSlug}` : `https://orevital.com/product/${productSlug}`} />
    <div className="min-h-dvh bg-zinc-50 pb-16" data-testid="dealer-product-page">
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 h-24 flex items-center gap-3">
          <Link to={`/d/${slug}`} className="flex items-center gap-2 shrink-0">
            <BrandLogo alt={brand.company} />
          </Link>
          <nav className="ml-auto flex items-center gap-3 sm:gap-4">
            <Link
              to={`/d/${slug}`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-700 hover:text-violet-700 transition-colors"
              data-testid="product-nav-brands"
            >
              <ShoppingBag size={14} />
              Shop
            </Link>
            <Link
              to={`/d/${slug}?view=home`}
              className="text-sm font-semibold text-zinc-700 hover:text-violet-700 transition-colors"
              data-testid="product-nav-about"
            >
              About Us
            </Link>
            <button
              onClick={() => setCartOpen(true)}
              className="relative h-10 w-10 rounded-full bg-violet-600 hover:bg-violet-700 text-white flex items-center justify-center shadow-sm"
              data-testid="cart-btn"
              aria-label="Cart"
            >
              <ShoppingCart size={16} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-amber-400 text-zinc-900 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            {user ? (
              <Link
                to="/app/home"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 text-xs font-semibold text-zinc-800"
                data-testid="topnav-account"
              >
                Hi, {user.first_name || "you"}
              </Link>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:underline"
                data-testid="product-signin"
              >
                Sign in →
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5 space-y-5">
        {/* Hero block */}
        <section className="grid md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-3">
            <div
              className="aspect-square rounded-2xl bg-white border border-zinc-200 flex items-center justify-center overflow-hidden"
              data-testid="product-main-media"
            >
              {current?.type === "video" ? (
                <video
                  key={current.url}
                  src={current.url}
                  controls
                  playsInline
                  className="w-full h-full object-contain bg-black"
                  data-testid="product-video-player"
                />
              ) : current?.type === "embed" ? (
                <iframe
                  key={current.url}
                  src={toEmbedUrl(current.url)}
                  title={product.name}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="product-video-embed"
                />
              ) : current?.url ? (
                <img
                  src={current.url}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              ) : (
                <Package size={64} className="text-zinc-300" />
              )}
            </div>

            {hasGallery && (
              <div
                className="flex items-center gap-2.5 overflow-x-auto pb-1"
                data-testid="product-thumbnail-strip"
              >
                {media.map((m, i) => {
                  const isActive = current?.url === m.url;
                  return (
                    <button
                      key={`${m.type}-${i}`}
                      type="button"
                      onClick={() => setActiveMedia(m)}
                      className={`relative shrink-0 h-16 w-16 rounded-xl overflow-hidden bg-white border-2 transition-colors ${
                        isActive
                          ? "border-violet-600 ring-1 ring-violet-200"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      data-testid={`product-thumbnail-${i}`}
                      aria-label={
                        m.type === "video" ? "Play product video" : `View image ${i + 1}`
                      }
                    >
                      {m.type === "video" || m.type === "embed" ? (
                        <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
                          <Play size={20} className="text-white fill-white" />
                        </div>
                      ) : (
                        <img
                          src={m.url}
                          alt={`${product.name} ${i + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) =>
                            (e.currentTarget.style.display = "none")
                          }
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <h1
              className="text-2xl sm:text-3xl font-bold text-zinc-900 leading-tight tracking-tight"
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              }}
              data-testid="product-name"
            >
              {product.name}
            </h1>
            {product.excerpt && (
              <p className="text-sm text-zinc-700 mt-2 font-medium leading-relaxed">
                {product.excerpt}
              </p>
            )}
            {product.blurb && (
              <p className="text-sm text-zinc-600 mt-1.5">{product.blurb}</p>
            )}

            {product.promo && product.promo.active && (
              <span
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-rose-100 text-rose-700 self-start"
                data-testid="product-promo-badge"
              >
                🎁 {product.promo.label}
              </span>
            )}

            {/* Rating */}
            <div className="flex items-center gap-1 mt-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  size={14}
                  className={
                    i <= Math.round(product.rating_avg || 5)
                      ? "fill-amber-400 text-amber-400"
                      : "text-zinc-300"
                  }
                />
              ))}
              <span className="text-xs text-zinc-500 ml-1">
                {product.rating_count > 0
                  ? `${Number(product.rating_avg || 5).toFixed(1)} (${product.rating_count})`
                  : "Be the first to review"}
              </span>
            </div>

            {/* Pricing block */}
            <div className="mt-5 rounded-2xl bg-white border border-zinc-200 p-4 space-y-1.5">
              {saleInfo.active && (
                <div className="text-[10px] uppercase tracking-widest font-bold text-rose-600">
                  Sale ends {saleInfo.endsDisplay}
                </div>
              )}
              <div className="flex items-baseline gap-3 flex-wrap">
                <span
                  className="text-3xl font-extrabold text-violet-700"
                  style={{
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  }}
                  data-testid="product-final-price"
                >
                  {fmtUSD(finalPrice)}
                </span>
                {(saleInfo.active || showRolePrice) && (
                  <span className="text-sm text-zinc-400 line-through">
                    {fmtUSD(msrp)}
                  </span>
                )}
              </div>
              {showRolePrice && !saleInfo.active && (
                <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                  Your {tier} price
                </div>
              )}
              {saleInfo.active && showRolePrice && (
                <div className="text-[11px] text-zinc-500">
                  Reg. {tier} price {fmtUSD(rolePrice)}
                </div>
              )}
              <div className="text-[11px] text-zinc-500 pt-1">
                {Number(product.inventory || 0) > 0 ? (
                  <span className="text-emerald-600 font-semibold">
                    In stock
                  </span>
                ) : (
                  <span className="text-rose-600 font-semibold">
                    Out of stock
                  </span>
                )}
              </div>
            </div>

            {/* Qty + Add */}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex items-center rounded-xl border border-zinc-200 bg-white overflow-hidden">
                <button
                  onClick={() => setQty((q) => Math.max(1, (Number(q) || 1) - 1))}
                  className="h-11 w-11 text-zinc-600 hover:bg-zinc-50 text-lg"
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  value={qty}
                  onChange={(e) =>
                    setQty(Math.max(1, Number(e.target.value) || 1))
                  }
                  className="w-12 h-11 text-center text-sm font-semibold bg-white outline-none"
                  data-testid="product-qty"
                />
                <button
                  onClick={() => setQty((q) => (Number(q) || 1) + 1)}
                  className="h-11 w-11 text-zinc-600 hover:bg-zinc-50 text-lg"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                disabled={Number(product.inventory || 0) <= 0}
                className="flex-1 h-11 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
                data-testid="product-add-cart"
              >
                <ShoppingCart size={15} />
                Add to cart · {fmtUSD(finalPrice * (Number(qty) || 1))}
              </button>
            </div>
          </div>
        </section>

        {/* Reviews — full width, immediately after the hero */}
        <ProductReviews productRecordId={product.id} />

        {/* Full product HTML (post_content) */}
        {product.post_content_html && (() => {
          const { summary, hasMore } = splitFirstBlock(product.post_content_html);
          const showFull = descExpanded || !hasMore;
          return (
            <section
              className="rounded-2xl bg-white border border-zinc-200 p-4 sm:p-5"
              data-testid="product-description"
            >
              <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-3">
                Description
              </h2>
              <div
                className="prose prose-sm max-w-none text-zinc-800"
                // Airtable post_content is curated by the brand, not user input.
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(showFull ? product.post_content_html : summary),
                }}
              />
              {hasMore && (
                <button
                  onClick={() => setDescExpanded((v) => !v)}
                  className="mt-3 text-sm font-semibold text-violet-700 hover:underline"
                  data-testid="product-desc-toggle"
                >
                  {descExpanded ? "Read less" : "Read more"}
                </button>
              )}
            </section>
          );
        })()}

        {/* Ingredients */}
        {product.ingredients?.length > 0 && (
          <section
            className="rounded-2xl bg-white border border-zinc-200 p-4 sm:p-5"
            data-testid="product-ingredients"
          >
            <div className="flex items-center gap-2 mb-3">
              <Beaker size={16} className="text-violet-700" />
              <h2 className="text-sm font-bold uppercase tracking-widest text-violet-700">
                Key ingredients
              </h2>
              <span className="text-[11px] text-zinc-500">
                {product.ingredients.length} active
              </span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-2.5">
              {product.ingredients.map((ing) => (
                <li
                  key={ing.id || ing.name}
                  className="rounded-xl bg-zinc-50 border border-zinc-100 p-3 flex items-start gap-3"
                >
                  {ing.image_url ? (
                    <img
                      src={ing.image_url}
                      alt={ing.name}
                      className="w-12 h-12 rounded-lg object-cover bg-white shrink-0 ring-1 ring-zinc-100"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-zinc-900">
                      {ing.name}
                    </div>
                    {ing.benefit && (
                      <div className="text-xs text-zinc-600 mt-1 leading-snug">
                        {ing.benefit}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <CartSheet
        open={cartOpen}
        onOpenChange={setCartOpen}
        dealerSlug={slug}
        products={[product]}
        user={user}
      />
    </div>
    </>
  );
}

/**
 * Resolve the active sale (if any) for a given role/tier.
 *
 * The Airtable `price_details_id` JSON stores per-role sale data with
 * keys like `salePrice<Role>`, `_sale_price_dates_from_<role>`,
 * `_sale_price_dates_to_<role>`.  Role keys aren't case-consistent so
 * we try a few common variants.
 */
function resolveSale(saleMap, tier) {
  if (!saleMap || !tier) return { active: false };
  const variants = [tier, tier.toLowerCase(), capitalize(tier)];
  for (const v of variants) {
    const sale = saleMap[`salePrice${v}`];
    if (sale === undefined || sale === "" || Number(sale) <= 0) continue;
    const from = saleMap[`_sale_price_dates_from_${v}`];
    const to = saleMap[`_sale_price_dates_to_${v}`];
    if (isWithinSale(from, to)) {
      return {
        active: true,
        salePrice: Number(sale),
        endsDisplay: to ? formatDate(to) : "",
      };
    }
  }
  return { active: false };
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Convert a YouTube/Vimeo watch URL into an embeddable iframe URL.
function toEmbedUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (host.endsWith("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
      if (u.pathname.startsWith("/embed/")) return url;
    }
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch {
    /* ignore */
  }
  return url;
}

function isWithinSale(from, to) {
  if (!from && !to) return false;
  const now = new Date();
  if (from) {
    const f = new Date(from);
    if (!isNaN(f) && now < f) return false;
  }
  if (to) {
    const t = new Date(to);
    // Make 'to' inclusive of the full day
    if (!isNaN(t)) {
      const endOfDay = new Date(t);
      endOfDay.setHours(23, 59, 59, 999);
      if (now > endOfDay) return false;
    }
  }
  return true;
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(d);
  }
}

// Returns the first meaningful block (paragraph) of an HTML string as the
// collapsed "summary", plus whether there is more content to reveal.
// Airtable authors content wrapped in a single outer <section>/<div>, so we
// descend into single-child wrappers until we reach the real block list.
function splitFirstBlock(html) {
  if (!html) return { summary: "", hasMore: false };
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let container = doc.body;
    for (let i = 0; i < 5; i++) {
      const kids = Array.from(container.children).filter(
        (b) => (b.textContent || "").trim().length > 0
      );
      if (kids.length === 1 && kids[0].children.length > 0) {
        container = kids[0];
        continue;
      }
      break;
    }
    const blocks = Array.from(container.children);
    const idx = blocks.findIndex((b) => (b.textContent || "").trim().length > 0);
    if (idx === -1) return { summary: html, hasMore: false };
    const summary = blocks[idx].outerHTML;
    const hasMore = blocks
      .slice(idx + 1)
      .some((b) => (b.textContent || "").trim().length > 0);
    return { summary, hasMore };
  } catch {
    return { summary: html, hasMore: false };
  }
}
